// TP-Whats-New API client.
//
// Isolated from the shared services/api.js (small PR surface, no risk to the
// shared axios instance) — mirrors services/scoutRequestService.js. Paths +
// shapes are the fixed API contract (whats_new app), never guessed:
//   feed     → GET  /api/whats-new/posts/?page=N     (DRF-paginated)
//   visit    → POST /api/whats-new/visit/            ({previous_visit, had_unread})
//   unread   → GET  /api/whats-new/unread/           ({unread: bool}) — menu dot
//   react    → POST /api/whats-new/posts/<id>/react/ ({reaction}) — toggle/switch
//   vote     → POST /api/whats-new/posts/<id>/vote/  ({option_id}) — 400 if closed
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

// Same Bearer-token attachment the scout-request / club-network clients use.
authApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* no token — request proceeds and the server returns 401 */
  }
  return config;
});

const fail = (error) => ({
  success: false,
  status: error?.response?.status,
  error:
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (typeof error?.response?.data === "object" && error.response.data
      ? Object.values(error.response.data)[0]
      : null) ||
    "Something went wrong. Please try again.",
});

const isCancel = (error) =>
  error?.name === "CanceledError" || error?.name === "AbortError";

export const whatsNewService = {
  // Paginated feed for the requesting account (audience filtered server-side,
  // pinned first). Returns the raw DRF envelope so the page can drive load-more
  // off `next`.
  getPosts: async (page = 1, signal) => {
    try {
      const res = await authApi.get("/api/whats-new/posts/", {
        params: { page },
        signal,
      });
      return {
        success: true,
        results: res.data?.results || [],
        count: res.data?.count ?? 0,
        next: res.data?.next ?? null,
        previous: res.data?.previous ?? null,
      };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Lightweight boolean for the menu dot. Errors degrade silently to unread=false.
  getUnread: async (signal) => {
    try {
      const res = await authApi.get("/api/whats-new/unread/", { signal });
      return { success: true, unread: Boolean(res.data?.unread) };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Stamp the last-visit marker to now(); the response carries the PREVIOUS
  // marker so the feed can place the "New since your last visit" divider.
  visit: async () => {
    try {
      const res = await authApi.post("/api/whats-new/visit/", {});
      return {
        success: true,
        previousVisit: res.data?.previous_visit ?? null,
        hadUnread: Boolean(res.data?.had_unread),
      };
    } catch (error) {
      return fail(error);
    }
  },

  // Toggle/switch a reaction. Sending the key you already own removes it.
  // Server returns the fresh `reactions` block (counts + my_reaction).
  react: async (postId, reaction) => {
    try {
      const res = await authApi.post(
        `/api/whats-new/posts/${postId}/react/`,
        { reaction },
      );
      return { success: true, reactions: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Cast/change a poll vote while the poll is open. Server returns the fresh
  // `poll` block; a closed poll responds 400 (surfaced via fail()).
  vote: async (postId, optionId) => {
    try {
      const res = await authApi.post(
        `/api/whats-new/posts/${postId}/vote/`,
        { option_id: optionId },
      );
      return { success: true, poll: res.data };
    } catch (error) {
      return fail(error);
    }
  },
};

export default whatsNewService;
